import { LandingPageContent } from '../types';

/**
 * Greek-language pilot page, targeting «προσωποποιημένα παιδικά βιβλία» and
 * related queries searched from Greece and Cyprus in Greek. The first (and so
 * far only) non-English content page: it sets `lang: 'el'` so the page emits
 * <html lang="el"> and no en-hreflang. If it earns traffic, it becomes the
 * template for a proper localized content set (see the SEO backlog).
 */
const prosopopiimenaPaidikaVivlia: LandingPageContent = {
	path: '/prosopopoiimena-paidika-vivlia',
	lang: 'el',
	seoTitle: 'Προσωποποιημένα Παιδικά Βιβλία — Το Παιδί σας ο Ήρωας της Ιστορίας',
	metaDescription:
		'Προσωποποιημένα παιδικά βιβλία στα ελληνικά: το παιδί σας εικονογραφημένο ως ήρωας της δικής του ιστορίας, με το όνομά του σε κάθε σελίδα. Εγκρίνετε κάθε σελίδα πριν την εκτύπωση.',
	heading: 'Προσωποποιημένα Παιδικά Βιβλία',
	subheading: 'Το πρόσωπό του στις εικόνες. Το όνομά του στην ιστορία.',
	heroImage: '/assets/hero/kid-5/watercolor_scene.png',
	breadcrumbParents: [],
	ui: {
		faqHeading: 'Συχνές ερωτήσεις',
		homeLabel: 'Αρχική',
	},
	intro: `Κάθε παιδί έχει ακούσει εκατοντάδες παραμύθια για άλλους — αρκούδους, πριγκίπισσες, δεινόσαυρους. Ένα βιβλίο όμως στο οποίο ο ήρωας είναι *το ίδιο το παιδί* — με το πρόσωπό του στην εικονογράφηση και το όνομά του σε κάθε σελίδα — είναι κάτι που δεν το αφήνει από τα χέρια του.

Έτσι δουλεύει: ανεβάζετε μια φωτογραφία, εμείς εικονογραφούμε το παιδί σας — αναγνωρίσιμα *αυτό* — και το κάνουμε ήρωα μιας τυπωμένης ιστορίας στα ελληνικά. Εσείς εγκρίνετε κάθε σελίδα πριν τυπωθεί οτιδήποτε.

Δεν είναι απλώς ένα δώρο. Είναι το βιβλίο που θα δείχνει στη γιαγιά, στους φίλους, και — σε είκοσι χρόνια — στα δικά του παιδιά.`,
	sections: [
		{
			heading: 'Γιατί τα προσωποποιημένα βιβλία λειτουργούν',
			body: `Τα παιδιά δεν χρειάζονται πειθώ για να ενδιαφερθούν για έναν ήρωα που είναι ο εαυτός τους. Αυτό είναι όλο το μυστικό.

Ένα συνηθισμένο βιβλίο πρέπει πρώτα να κερδίσει το παιδί — να το πείσει ότι ο αρκούδος αξίζει την προσοχή του. Το προσωποποιημένο βιβλίο ξεκινά από τη γραμμή του τερματισμού: το παιδί γυρίζει τις σελίδες για να δει τι *έκανε μετά*. Γι' αυτό και ξαναδιαβάζεται ξανά και ξανά, πολύ μετά το σημείο όπου κάθε άλλο δώρο έχει ξεχαστεί.

Για παιδιά που μαθαίνουν ανάγνωση, υπάρχει και κάτι ακόμα: το όνομά τους είναι η πρώτη λέξη που αναγνωρίζουν γραμμένη. Ένα βιβλίο γεμάτο με το όνομά τους είναι εξάσκηση ανάγνωσης που τη ζητούν μόνα τους.`,
		},
		{
			heading: 'Πώς γίνεται',
			body: `1. **Ανεβάζετε μια φωτογραφία.** Μια καθαρή φωτογραφία από το κινητό αρκεί — το πρόσωπο ορατό και φωτισμένο.
2. **Διαλέγετε ιστορία και στυλ εικονογράφησης.** Ακουαρέλα, κλασικό παραμύθι, μοντέρνο επίπεδο σχέδιο ή τρισδιάστατο στυλ τύπου Pixar.
3. **Εγκρίνετε τις σελίδες.** Βλέπετε κάθε σελίδα πριν την εκτύπωση. Αν κάτι δεν σας αρέσει, ξαναδημιουργείται.
4. **Εκτύπωση και αποστολή.** Πραγματικό χαρτί, πραγματική βιβλιοδεσία, στην πόρτα σας — σε Ελλάδα, Κύπρο και όλο τον κόσμο.

Το όνομα του παιδιού αποδίδεται σωστά μέσα στην ιστορία — με τόνους και με τις πτώσεις του, γιατί μια ιστορία για τον Νίκο πρέπει να ξέρει πότε να πει «του Νίκου».`,
		},
		{
			heading: 'Για ποιες ηλικίες',
			body: `**2–3 ετών.** Η αναγνώριση είναι όλη η εμπειρία — το παιδί δείχνει τη σελίδα και λέει το όνομά του. Σύντομες ιστορίες με μεγάλες, καθαρές εικόνες.

**3–5 ετών.** Η χρυσή ηλικία. Το παιδί παρακολουθεί την πλοκή, τη μαθαίνει απέξω, και σας διορθώνει όταν πηδάτε σελίδες.

**6–8 ετών.** Πρώτοι αναγνώστες. Ένα βιβλίο όπου ο πρωταγωνιστής έχει το όνομά τους μετατρέπει την ανάγνωση από υποχρέωση σε προσωπική υπόθεση.

**9+ ετών.** Τα παραμύθια αρχίζουν να «μικραίνουν» — εδώ ταιριάζουν καλύτερα τα αναμνηστικά: μπλουζάκια, κούπες και αφίσες με τον εικονογραφημένο χαρακτήρα τους.`,
		},
		{
			heading: 'Οι περιστάσεις που ταιριάζει περισσότερο',
			body: `**Γενέθλια.** Το δώρο που δεν μπορεί να το έχει φέρει κανείς άλλος στο πάρτι.

**Ονομαστική εορτή.** Η γιορτή του *ονόματος* — και ένα βιβλίο με το όνομα του παιδιού σε κάθε σελίδα είναι ακριβώς στο θέμα της ημέρας.

**Βάπτιση.** Το κλασικό δίλημμα του νονού και της νονάς, λυμένο: ένα κειμήλιο με το όνομα που δόθηκε εκείνη τη μέρα, που όμως το παιδί θα το *χρησιμοποιεί* — όχι άλλο ένα ασημικό στο συρτάρι.

**Χριστούγεννα και Πάσχα.** Οι μεγάλες οικογενειακές στιγμές. Μια σημείωση: τα προσωποποιημένα δώρα φτιάχνονται κατά παραγγελία — για τα Χριστούγεννα, παραγγείλετε από τα τέλη Νοεμβρίου.

**Για τα Ελληνόπουλα του εξωτερικού.** Ένα βιβλίο στα ελληνικά, με ήρωα το ίδιο το παιδί, είναι από τους πιο αποτελεσματικούς τρόπους να μείνει η γλώσσα ζωντανή — γιατί κανένα παιδί δεν αδιαφορεί για μια ιστορία που μιλάει για εκείνο.`,
		},
	],
	picks: [
		{
			label: 'Όλα τα προϊόντα',
			href: '/products',
			description: 'Βιβλία, βιβλία ζωγραφικής, μπλουζάκια, κούπες και αφίσες.',
		},
		{
			label: 'Personalized story books',
			href: '/personalized-story-books',
			description: 'Ο πλήρης οδηγός μας για τα προσωποποιημένα βιβλία (στα αγγλικά).',
		},
	],
	faqs: [
		{
			question: 'Τι είναι ένα προσωποποιημένο παιδικό βιβλίο;',
			answer:
				'Ένα τυπωμένο βιβλίο στο οποίο το παιδί σας εμφανίζεται ως ο εικονογραφημένος ήρωας της ιστορίας — με το πρόσωπό του στις εικόνες, σχεδιασμένο με συνέπεια σε κάθε σελίδα, και το όνομά του μέσα στο κείμενο. Δημιουργείται από μία φωτογραφία που ανεβάζετε.',
		},
		{
			question: 'Η ιστορία είναι στα ελληνικά;',
			answer:
				'Ναι. Οι ιστορίες παράγονται στα ελληνικά — όχι με αυτόματη μετάφραση — και το όνομα του παιδιού αποδίδεται σωστά, με τόνους και πτώσεις. Διατίθενται και άλλες γλώσσες, ώστε το ίδιο βιβλίο να υπάρχει π.χ. στα ελληνικά και στα αγγλικά.',
		},
		{
			question: 'Βλέπω το βιβλίο πριν τυπωθεί;',
			answer:
				'Ναι. Εγκρίνετε κάθε σελίδα πριν από την εκτύπωση, και όποια σελίδα δεν σας ικανοποιεί ξαναδημιουργείται. Τίποτα δεν τυπώνεται χωρίς το «ναι» σας.',
		},
		{
			question: 'Πόσο νωρίς πρέπει να παραγγείλω;',
			answer:
				'Τα βιβλία φτιάχνονται κατά παραγγελία: δημιουργία εικονογράφησης, έγκριση από εσάς, εκτύπωση και αποστολή. Δύο έως τρεις εβδομάδες πριν την περίσταση είναι άνετο περιθώριο — για τα Χριστούγεννα, παραγγείλετε από τα τέλη Νοεμβρίου.',
		},
		{
			question: 'Στέλνετε εκτός Ελλάδας;',
			answer:
				'Ναι, τα βιβλία εκτυπώνονται και αποστέλλονται διεθνώς — στην Κύπρο, την Ευρώπη, τις ΗΠΑ και την Αυστραλία. Οι οικογένειες της ομογένειας είναι μεγάλο μέρος των παραγγελιών μας.',
		},
	],
	related: [
		{ label: 'Personalized Greek books for kids (English)', href: '/personalized-greek-books-for-kids' },
		{ label: 'Christening gifts', href: '/gifts/christening-gifts' },
	],
	cta: { label: 'Φτιάξτε το βιβλίο του παιδιού σας', href: '/products' },
};

export default prosopopiimenaPaidikaVivlia;
